// AP-MED-05 — پنل‌های خدمات تکمیلی تامین که داخل صفحه‌های موجود می‌نشینند:
//
// • `InsurerPatientFlagsPanel` → SCR-STAFF-700 (US-MED-097): نشان‌های بیمه‌گر مراجعه‌کننده و ثبت نشان تازه.
// • `ReferentialServiceDialog` → COMP-RX-EDITOR (US-MED-095): واکشی نسخه خدمات ارجاعی پزشک اول با کد رهگیری و ثبت
//   «انجام شد» توسط پزشک دوم.
//
// نام بیماری و نام خدمت داده **Clinical**‌اند: Permission لازم است و هر مشاهده در سرور با `RecordView` ثبت می‌شود.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type MedPatientFlagDiseaseView,
  type MedReferentialServiceItemView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, toJalaliDateTime, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SUPPORT_LEVELS, supplementaryKeys, SupplementarySampleBadge, SupplementaryStatus, UnsupportedNotice } from './supplementary';

/**
 * نشان‌های بیمه‌گر مراجعه‌کننده (SCR-STAFF-700؛ AC-MED-097.1، 097.2): فهرست بیماری‌های مجاز **از سامانه** می‌آید و
 * بیماری بیرون آن ثبت نمی‌شود (PHX-MED-0337). سطح حمایت فقط برای بیماری نیازمند آن نمایش داده و الزامی می‌شود
 * (PHX-MED-0338).
 */
export function InsurerPatientFlagsPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [open, setOpen] = useState(false);
  const [illId, setIllId] = useState('');
  const [supportLevel, setSupportLevel] = useState<number | ''>('');

  const tenantId = session?.tenantId;
  const canManage = access.can('med.patient_flag.manage');

  const flags = useQuery({
    queryKey: supplementaryKeys.flags(tenantId, patientId),
    enabled: Boolean(tenantId) && canManage,
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/insurer-flags', { params: { path: { patientId } } })),
  });

  const diseases = useQuery({
    queryKey: supplementaryKeys.diseases(tenantId, patientId),
    enabled: Boolean(tenantId) && canManage && open,
    retry: false,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/medical-orders/patients/{patientId}/insurer-flags/diseases', { params: { path: { patientId } } }),
      ),
  });

  const chosen: MedPatientFlagDiseaseView | undefined = diseases.data?.diseases.find((row) => row.illId === illId);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/patients/{patientId}/insurer-flags', {
          params: { path: { patientId } },
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            patientId,
            insurer: 'TAMIN',
            illId,
            // بیماری بی‌سطح، سطح نمی‌گیرد: مقدار اضافی به سامانه نمی‌رود (§۴.۱۱).
            supportLevel: chosen?.requiresSupportLevel && supportLevel !== '' ? supportLevel : null,
          },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error ?? undefined);
      void queryClient.invalidateQueries({ queryKey: supplementaryKeys.flags(tenantId, patientId) });
    },
    onSuccess: () => {
      setOpen(false);
      setIllId('');
      setSupportLevel('');
    },
  });

  if (!canManage) {
    return null;
  }

  const unsupported = isApiError(diseases.error) && diseases.error.code === 'PHX-MED-0339';

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="med-patient-flags">
      <Stack spacing={1}>
        <Typography variant="subtitle2">{t('med:patient_flags.title')}</Typography>
        {(flags.data?.flags ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary" data-testid="med-patient-flags-empty">
            {t('med:patient_flags.empty')}
          </Typography>
        ) : null}

        {(flags.data?.flags ?? []).map((flag) => (
          <Stack key={flag.patientFlagId} direction="row" spacing={1} alignItems="center" data-testid={`med-patient-flag-${flag.patientFlagId}`}>
            <SupplementaryStatus status={flag.status} />
            <Typography variant="body2">{flag.illName ?? flag.illId}</Typography>
            {flag.supportLevel ? (
              <Typography variant="caption" color="text.secondary" data-testid={`med-patient-flag-level-${flag.patientFlagId}`}>
                {t('med:patient_flags.support_level_value', { level: flag.supportLevel })}
              </Typography>
            ) : null}
            {flag.trackingCode ? (
              <Typography variant="caption" color="text.secondary">
                {flag.trackingCode}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {toJalaliDateTime(flag.createdAt, { timeZone })}
            </Typography>
          </Stack>
        ))}

        <Stack direction="row">
          <Button size="small" onClick={() => setOpen(true)} data-testid="med-patient-flag-add">
            {t('med:patient_flags.add')}
          </Button>
        </Stack>
      </Stack>

      <FormDialog
        open={open}
        titleKey="med:patient_flags.add"
        screenId="SCR-STAFF-700"
        onClose={() => setOpen(false)}
        onSubmit={() => {
          // §۴.۱۱: بیماری انتخاب‌نشده و اوتیسم بی سطح حمایت هرگز فرستاده نمی‌شوند.
          if (illId.length > 0 && (chosen?.requiresSupportLevel !== true || supportLevel !== '')) {
            save.mutate();
          }
        }}
        isDirty={illId.length > 0}
        isSubmitting={save.isPending}
        submitError={save.error}
      >
        <Stack spacing={2}>
          {unsupported ? <UnsupportedNotice serviceKey="patient_flags" /> : null}
          {diseases.error && !unsupported ? <ErrorState error={diseases.error} /> : null}

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {t('med:patient_flags.from_insurer')}
            </Typography>
            <SupplementarySampleBadge isSample={diseases.data?.isSample} />
          </Stack>

          <TextField
            select
            label={t('med:patient_flags.disease')}
            value={illId}
            onChange={(event) => {
              setIllId(event.target.value);
              setSupportLevel('');
            }}
            slotProps={{ htmlInput: { 'data-testid': 'med-patient-flag-disease' } }}
            fullWidth
          >
            {(diseases.data?.diseases ?? []).map((disease) => (
              <MenuItem key={disease.illId} value={disease.illId}>
                {disease.name ?? disease.text ?? disease.illId}
              </MenuItem>
            ))}
          </TextField>

          {chosen?.requiresSupportLevel ? (
            <TextField
              select
              required
              label={t('med:patient_flags.support_level')}
              helperText={t('med:patient_flags.support_level_hint')}
              value={supportLevel}
              onChange={(event) => setSupportLevel(Number(event.target.value))}
              slotProps={{ htmlInput: { 'data-testid': 'med-patient-flag-support-level' } }}
              fullWidth
            >
              {SUPPORT_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {t(`med:patient_flags.level.${level}`)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

        </Stack>
      </FormDialog>
    </Paper>
  );
}

/** شناسه ریز نسخه نزد بیمه‌گر (`int64` قرارداد؛ مولد آن را `string | number` می‌دهد). */
type ReferentialItemId = MedReferentialServiceItemView['providerItemId'];

/**
 * ثبت انجام خدمات ارجاعی توسط پزشک دوم (COMP-RX-EDITOR؛ AC-MED-095.2): کد رهگیری نسخه پزشک اول ← واکشی اقلام ←
 * انتخاب «انجام شد» ← نسخه نوع ۵ در صف ارسال ملی، پیوندخورده به مواجهه جاری.
 */
export function ReferentialServiceDialog({
  open,
  encounterId,
  patientId,
  onClose,
}: {
  open: boolean;
  encounterId: string;
  patientId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('med');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [trackingCode, setTrackingCode] = useState('');
  const [items, setItems] = useState<readonly MedReferentialServiceItemView[]>([]);
  // `providerItemId` در قرارداد `int64` است و مولد آن را `string | number` می‌دهد، پس حالت هم همان را نگه می‌دارد.
  const [selected, setSelected] = useState<readonly ReferentialItemId[]>([]);

  const lookup = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/referential-services:lookup', {
          body: { patientId, insurer: 'TAMIN', trackingCode: trackingCode.trim() },
        }),
      ),
    onSuccess: (response) => {
      setItems(response.items);
      // قلمی که سامانه «اقدام‌شده» اعلام کرده پیش‌انتخاب نمی‌شود.
      setSelected(response.items.filter((item) => item.isActioned !== true).map((item) => item.providerItemId));
    },
  });

  const perform = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/referential-services:perform', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            encounterId,
            insurer: 'TAMIN',
            trackingCode: trackingCode.trim(),
            items: items
              .filter((item) => selected.includes(item.providerItemId))
              .map((item) => ({
                providerItemId: item.providerItemId,
                referenceStatus: item.referenceStatus ?? null,
                serviceCode: item.serviceCode ?? '',
                serviceName: item.serviceName ?? null,
                quantity: item.quantity ?? 1,
              })),
          },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setItems([]);
      setSelected([]);
      setTrackingCode('');
      onClose();
    },
  });

  const unsupported = isApiError(lookup.error) && lookup.error.code === 'PHX-MED-0339';

  return (
    <FormDialog
      open={open}
      titleKey="med:referential.title"
      screenId="COMP-RX-EDITOR"
      maxWidth="md"
      onClose={onClose}
      onSubmit={() => {
        if (selected.length > 0) {
          perform.mutate();
        }
      }}
      isDirty={selected.length > 0}
      isSubmitting={perform.isPending}
      submitError={perform.error}
    >
      <Stack spacing={2}>
        <Alert severity="info">{t('med:referential.description')}</Alert>
        {unsupported ? <UnsupportedNotice serviceKey="referential_services" /> : null}

        <Stack direction="row" spacing={1}>
          <TextField
            label={t('med:referential.tracking_code')}
            value={trackingCode}
            onChange={(event) => setTrackingCode(event.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'med-referential-tracking' } }}
            fullWidth
          />
          <Button
            variant="outlined"
            disabled={trackingCode.trim().length === 0 || lookup.isPending}
            onClick={() => lookup.mutate()}
            data-testid="med-referential-lookup"
          >
            {t('med:referential.lookup')}
          </Button>
        </Stack>

        {lookup.error && !unsupported ? <ErrorState error={lookup.error} /> : null}

        {items.map((item) => (
          <FormControlLabel
            key={item.providerItemId}
            control={
              <Checkbox
                checked={selected.includes(item.providerItemId)}
                disabled={item.isActioned === true}
                onChange={() =>
                  setSelected((current: readonly ReferentialItemId[]) =>
                    current.includes(item.providerItemId) ? current.filter((id) => id !== item.providerItemId) : [...current, item.providerItemId],
                  )
                }
                inputProps={{ 'aria-label': item.serviceName ?? String(item.providerItemId) }}
                data-testid={`med-referential-item-${item.providerItemId}`}
              />
            }
            label={
              <Typography variant="body2">
                {item.serviceName ?? item.serviceCode ?? '—'}
                {item.quantity ? ` × ${item.quantity}` : ''}
              </Typography>
            }
          />
        ))}

        {lookup.data ? <SupplementarySampleBadge isSample={lookup.data.isSample} /> : null}
      </Stack>
    </FormDialog>
  );
}
